/** Presentation helpers for the API-credentials grid + drawer. */
import { Chip } from '@mui/material';
import type { ChipProps } from '@mui/material';

import type {
  ApiCredentialScope,
  ApiCredentialStatus,
  ApiCredentialType,
} from '@/api/apiCredentials';

export const CREDENTIAL_STATUSES: ApiCredentialStatus[] = ['active', 'disabled', 'revoked'];

export const CREDENTIAL_SCOPES: ApiCredentialScope[] = [
  'client',
  'admin',
  'tour_operator',
  'webhooks',
  'partner_dev',
];

export const CREDENTIAL_TYPES: ApiCredentialType[] = ['opaque', 'jwt_refresh'];

const STATUS_COLORS: Record<ApiCredentialStatus, ChipProps['color']> = {
  active: 'success',
  disabled: 'warning',
  revoked: 'error',
};

/** Credential status as a colored chip. */
export function StatusChip({ status }: { status: ApiCredentialStatus }) {
  const color = STATUS_COLORS[status] ?? 'default';
  return (
    <Chip
      size="small"
      color={color}
      variant={color === 'default' ? 'outlined' : 'filled'}
      label={status}
      sx={{ height: 22, fontSize: 12 }}
    />
  );
}

/** Credential scope chip (which API surface accepts the key). */
export function ScopeChip({ scope }: { scope: ApiCredentialScope }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={scope.replace(/_/g, ' ')}
      sx={{ height: 22, fontSize: 12 }}
    />
  );
}

/** "Legacy" marker: the row stores a plaintext legacy key (revealable). */
export function LegacyChip() {
  return (
    <Chip
      size="small"
      color="warning"
      variant="outlined"
      label="legacy"
      sx={{ height: 22, fontSize: 12 }}
    />
  );
}

/** Tenant column value for system/platform credentials (no owning tenant). */
export function SystemChip() {
  return (
    <Chip size="small" variant="outlined" label="System" sx={{ height: 22, fontSize: 12, fontStyle: 'italic' }} />
  );
}
